import type { KullaniciRol } from "../mocks/kullanicilar";
import type { ApiUser } from "./api";
import {
  clearApiToken,
  isAuthError,
  meRequest,
  writeApiToken,
} from "./api";

export const SESSION_KEY = "evren_user";
export const SESSION_TOKEN_KEY = "evren_token";

export interface SessionUser {
  id: number;
  adSoyad: string;
  rol: KullaniciRol;
  customerId?: number;
  forcePasswordChange?: boolean;
  smsOperationalOptIn?: boolean | null;
  smsMarketingOptIn?: boolean | null;
}

export function mapApiUserToSessionUser(user: ApiUser): SessionUser {
  return {
    id: user.id,
    adSoyad: user.ad_soyad,
    rol: user.rol,
    customerId:
      typeof user.customer_id === "number" ? user.customer_id : undefined,
    forcePasswordChange: user.force_password_change,
    smsOperationalOptIn:
      user.sms_operational_opt_in === undefined
        ? null
        : user.sms_operational_opt_in,
    smsMarketingOptIn:
      user.sms_marketing_opt_in === undefined
        ? null
        : user.sms_marketing_opt_in,
  };
}

export function readSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(SESSION_TOKEN_KEY);
}

export function writeSessionToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SESSION_TOKEN_KEY, token);
  writeApiToken(token);
}

export function readSessionUser(): SessionUser | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.id !== "number") return null;
    if (typeof parsed.adSoyad !== "string") return null;
    if (typeof parsed.rol !== "string") return null;

    return {
      id: parsed.id,
      adSoyad: parsed.adSoyad,
      rol: parsed.rol,
      customerId:
        typeof parsed.customerId === "number" ? parsed.customerId : undefined,
      forcePasswordChange:
        typeof parsed.forcePasswordChange === "boolean"
          ? parsed.forcePasswordChange
          : false,
      smsOperationalOptIn:
        parsed.smsOperationalOptIn === true ||
        parsed.smsOperationalOptIn === false
          ? parsed.smsOperationalOptIn
          : null,
      smsMarketingOptIn:
        parsed.smsMarketingOptIn === true || parsed.smsMarketingOptIn === false
          ? parsed.smsMarketingOptIn
          : null,
    };
  } catch {
    return null;
  }
}

export function writeSessionUser(user: SessionUser) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function writeSessionFromApi(user: ApiUser, token: string) {
  writeSessionToken(token);
  writeSessionUser(mapApiUserToSessionUser(user));
}

export function clearSessionUser() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(SESSION_KEY);
  window.localStorage.removeItem(SESSION_TOKEN_KEY);
  clearApiToken();
}

export function getDefaultPathForRole(rol: KullaniciRol) {
  if (rol === "Müşteri") return "/musteri-panel";
  if (rol === "Kesim Alanı") return "/panel/kesim-alani";
  if (rol === "Taşıyıcı") return "/panel/tasiyici-tasima";
  if (rol === "Sıra Operatörü") return "/panel/istasyon/sira";
  if (rol === "Kesim Operatörü") return "/panel/istasyon/kesim";
  if (rol === "Parçalama Operatörü") return "/panel/istasyon/parcalama";
  if (rol === "Tamamlama Operatörü") return "/panel/istasyon/tamamlama";
  return "/panel/hayvanlar";
}

export function mustForcePasswordChange(
  user?: Pick<
    SessionUser,
    "rol" | "forcePasswordChange" | "smsOperationalOptIn" | "smsMarketingOptIn"
  > | null
) {
  if (!user) return false;

  if (user.forcePasswordChange) return true;

  if (user.rol === "Müşteri") {
    if (
      user.smsOperationalOptIn === null ||
      user.smsOperationalOptIn === undefined
    ) {
      return true;
    }

    if (
      user.smsMarketingOptIn === null ||
      user.smsMarketingOptIn === undefined
    ) {
      return true;
    }
  }

  return false;
}

export async function restoreSessionFromApi(): Promise<SessionUser | null> {
  const token = readSessionToken();
  const fallbackUser = readSessionUser();

  if (!token) {
    return null;
  }

  try {
    const user: ApiUser = await meRequest();
    const mapped = mapApiUserToSessionUser(user);
    writeSessionUser(mapped);
    return mapped;
  } catch (error) {
    if (isAuthError(error)) {
      clearSessionUser();
      return null;
    }

    return fallbackUser;
  }
}